/**
 * "Import a folder or zip" as a new project: what was picked or dropped → its files laid out as services
 * (detectServices.js) → a new sandbox project made the way New project makes one (quickProjectCreation.js) → the files
 * packed and sent with the tree's upload (treeUpload/runUpload.js: one archive, one commit, progress, Cancel).
 *
 * An archive is unpacked in the browser (treeUpload/readArchive.js) rather than sent as it is, so its layout and skips
 * are applied before anything is sent and the person sees and confirms them; vfs-service checks every path again.
 */
import { buildPlan, exclusionReason } from './treeUpload/uploadPlan';
import { ArchiveError, archiveKind, readArchive, SUPPORTED_ARCHIVES_HINT } from './treeUpload/readArchive';
import { readBytes, runUpload } from './treeUpload/runUpload';
import { declaresWorkspaces, infoSources, isBuildOutput, planLayout, projectInfo } from './detectServices';
import { createQuickVfsProject } from './quickProjectCreation';
import * as vfsClient from './vfsClient';

/** Why a file is left out of an import: what the tree upload leaves out, plus build output. null = imported. */
export function importSkipReason(relPath) {
  return exclusionReason(relPath, { skipHeavy: true }) || (isBuildOutput(relPath) ? 'build-output' : null);
}

export const SKIP_LABELS = {
  junk: 'system junk (.DS_Store, __MACOSX)',
  'skipped-folder': 'inside node_modules or .git',
  'build-output': 'build output (dist, build, __pycache__, …)',
  unsafe: 'unsafe paths',
  reserved: 'inside .git',
};

/** The one archive in what was picked or dropped, or null (a folder, several files). */
export function singleArchive(collected) {
  const files = collected?.files || [];
  if (files.length !== 1 || (collected.folderNames || []).length) { return null; }
  return archiveKind(files[0].relPath) ? files[0] : null;
}

/**
 * The files to import from a drop or a pick: a single archive is unpacked (zip, tar, tar.gz; others refused), anything
 * else is taken as it is. `archiveName` is the archive's file name ('' for a folder).
 */
export async function collectSource(collected, { signal, onProgress } = {}) {
  const archive = singleArchive(collected);
  if (!archive) {
    return { entries: collected?.files || [], archiveName: '', unreadable: collected?.unreadable || [] };
  }
  if (archiveKind(archive.relPath) === 'unsupported') {
    throw new ArchiveError(`${SUPPORTED_ARCHIVES_HINT}. Re-pack ${archive.relPath} as a .zip and try again.`, 'unsupported');
  }
  const unpacked = await readArchive(archive.file, { name: archive.relPath, signal, onProgress, skip: (relPath) => Boolean(importSkipReason(relPath)) });
  return {
    entries: unpacked.files,
    archiveName: archive.relPath.split('/').pop(),
    skippedInArchive: unpacked.skipped,
    unreadable: unpacked.unreadable,
  };
}

const MAX_INFO_BYTES = 256 * 1024;
async function readText(entry) {
  if (!entry?.file || entry.size > MAX_INFO_BYTES) { return ''; }
  try {
    return new TextDecoder().decode(await readBytes(entry.file));
  } catch {
    return '';
  }
}

/**
 * What the import will do, before anything is created: the layout (services and where every file goes), the
 * project's suggested name and description, the files and what is left out, and whether it fits one upload.
 */
export async function prepareImport({ entries, archiveName = '', skippedInArchive = [], unreadable = [] }) {
  const kept = [];
  const excluded = skippedInArchive.map((relPath) => ({ relPath, reason: importSkipReason(relPath) || 'skipped-folder' }));
  for (const entry of entries) {
    const reason = importSkipReason(entry.relPath);
    if (reason) { excluded.push({ relPath: entry.relPath, reason }); } else { kept.push(entry); }
  }
  const relPaths = kept.map((entry) => entry.relPath);
  const byPath = new Map(kept.map((entry) => [entry.relPath, entry]));

  let layout = planLayout(relPaths);
  let sources = infoSources(relPaths, layout);
  if (sources.rootPackageJson && declaresWorkspaces(await readText(byPath.get(sources.rootPackageJson)))) {
    layout = planLayout(relPaths, { rootIsWorkspace: true });
    sources = infoSources(relPaths, layout);
  }
  const [metadata, readme, packageJson] = await Promise.all([sources.metadata, sources.readme, sources.packageJson].map((path) => (path ? readText(byPath.get(path)) : '')));
  const info = projectInfo({ metadata, readme, packageJson }, { rootFolder: layout.rootFolder, archiveName });

  const files = [];
  for (const entry of kept) {
    const dest = layout.destOf(entry.relPath);
    if (dest) { files.push({ relPath: dest, size: entry.size || 0, file: entry.file }); }
  }
  const plan = buildPlan(files);
  return { layout, info, plan, excluded: [...excluded, ...plan.excluded], unreadable, archiveName };
}

/**
 * Creates the project and sends the planned files. Resolves to `{ projectId, name, tenantId, services, cancelled,
 * failed, serverMayHaveSaved }`; throws only when the project itself could not be created. Cancelling after the project
 * exists leaves it with what arrived (the caller says so and offers to open it).
 */
export async function runImport({ tenantId, name, description = '', prepared, signal, onProgress }) {
  if (signal?.aborted) { return { cancelled: true }; }
  onProgress?.({ phase: 'creating', results: {} });
  const created = await createQuickVfsProject(tenantId, name, null, { registerDefaultService: false });
  const { projectId } = created;
  if (description) {
    await vfsClient.updateProjectMetadata(projectId, { name: created.name || name, description }).catch(() => null);
  }
  const sent = await importIntoProject({ projectId, prepared, signal, onProgress });
  return { projectId, name: created.name || name, tenantId, ...sent };
}

/**
 * Sends the planned files into a project that exists (a brand-new one's Home, "Start by… importing a folder or a zip",
 * or the one runImport just made). Resolves to `{ services, results, cancelled, failed, serverMayHaveSaved }`.
 */
export async function importIntoProject({ projectId, prepared, signal, onProgress }) {
  const services = prepared.layout.services.map((service) => `services/${service.name}`);
  // The project's services, known before the files arrive: Code and the Dashboard list them straight away.
  for (const service of services) { vfsClient.registerVfsService(projectId, service); }
  const upload = await runUpload({ projectId, vfsTarget: '', files: prepared.plan.files, conflict: 'overwrite', signal, onProgress });
  const failed = Object.values(upload.results).filter((line) => line.status === 'failed').length;
  return { services, results: upload.results, cancelled: upload.cancelled, failed, serverMayHaveSaved: upload.serverMayHaveSaved };
}
